/**
 * Configuration state manager (singleton)
 * Manages API URL, Turnstile site key, and environment
 */

import type { AppConfig, StateListener, UnsubscribeFunction } from '../types';
import { getItem, setItem } from '../utils/storage';

const STORAGE_KEY = 'admin-ui-config';

const DEFAULT_CONFIG: AppConfig = {
  apiBaseUrl: 'https://api.consenger.app',
  turnstileSiteKey: '',
  environment: 'production',
};

export class ConfigStore {
  private static instance: ConfigStore;
  private config: AppConfig;
  private listeners: Set<StateListener<AppConfig>> = new Set();

  private constructor() {
    this.config = { ...DEFAULT_CONFIG };
    this.loadFromStorage();
  }

  static getInstance(): ConfigStore {
    if (!ConfigStore.instance) {
      ConfigStore.instance = new ConfigStore();
    }
    return ConfigStore.instance;
  }

  getConfig(): AppConfig {
    return { ...this.config };
  }

  setApiBaseUrl(url: string): void {
    this.config.apiBaseUrl = url;
    this.saveToStorage();
    this.notifyListeners();
  }

  setTurnstileSiteKey(key: string): void {
    this.config.turnstileSiteKey = key;
    this.saveToStorage();
    this.notifyListeners();
  }

  setEnvironment(env: 'production' | 'staging' | 'local'): void {
    this.config.environment = env;

    // Update API URL based on environment
    const urls: Record<string, string> = {
      production: 'https://api.consenger.app',
      staging: 'https://api-staging.consenger.app',
      local: 'http://localhost:8787',
    };
    this.config.apiBaseUrl = urls[env];

    this.saveToStorage();
    this.notifyListeners();
  }

  subscribe(listener: StateListener<AppConfig>): UnsubscribeFunction {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  loadFromStorage(): void {
    const saved = getItem<AppConfig>(STORAGE_KEY);
    if (saved) {
      this.config = { ...DEFAULT_CONFIG, ...saved };
    }
  }

  private saveToStorage(): void {
    setItem(STORAGE_KEY, this.config);
  }

  private notifyListeners(): void {
    const config = this.getConfig();
    this.listeners.forEach((listener) => listener(config));
  }
}
